// libs/shared/ui/builder/src/lib/components/auth/AgentAuthForgotId.tsx
import React, { useRef, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { AuthView } from "./AgentAuthSignIn";

interface Props {
  esignatureApiUrl: string;
  onNavigate: (view: AuthView) => void;
}

export default function AgentAuthForgotId({
  esignatureApiUrl,
  onNavigate,
}: Props) {
  const [npn, setNpn] = useState("");
  const [npnError, setNpnError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [apiError, setApiError] = useState("");
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (submitted && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [submitted]);

  function validate(): boolean {
    const trimmed = npn.trim();
    if (!trimmed) {
      setNpnError("NPN is required");
      return false;
    }
    if (trimmed.length > 30) {
      setNpnError("NPN must be 30 characters or fewer");
      return false;
    }
    if (!/^\d+$/.test(trimmed)) {
      setNpnError("NPN must contain only numbers");
      return false;
    }
    setNpnError("");
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setApiError("");
    try {
      const res = await fetch(
        `${esignatureApiUrl}/emails/send-for-agent/${encodeURIComponent(
          npn.trim()
        )}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            from: "memberservices@pivothealth.com",
            subject: "Agent vendor identifier",
            log_information: true,
          }),
        }
      );
      if (!res.ok) {
        setApiError(
          "We could not find an agent with that NPN. Please check your NPN and try again."
        );
        return;
      }
      setSubmitted(true);
    } catch {
      setApiError(
        "Request failed. Please check your connection and try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Stack spacing={2} maxWidth={400} width="100%">
        <Typography
          variant="h5"
          fontWeight={700}
          component="h1"
          tabIndex={-1}
          ref={successHeadingRef}
        >
          Check your email
        </Typography>
        <Typography>
          We sent your Agent ID to the email address associated with your NPN.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Did not receive an email? Call{" "}
          <Link
            href="tel:+18665662707"
            aria-label="Call support at (866) 566-2707"
          >
            (866) 566-2707
          </Link>
          , Option 3, then Option 1.
        </Typography>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => onNavigate("signin")}
        >
          Back to sign in
        </Link>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={2}
      maxWidth={400}
      width="100%"
      component="form"
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby="forgot-agent-id-heading"
      aria-busy={submitting}
    >
      {/* Pre-rendered live region — always in DOM; children toggled */}
      <div role="alert" aria-live="assertive" aria-atomic="true">
        {apiError && <Alert severity="error">{apiError}</Alert>}
      </div>

      <div>
        <Typography
          id="forgot-agent-id-heading"
          variant="h5"
          fontWeight={700}
          component="h1"
        >
          Recover your Agent ID
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Enter your NPN (National Producer Number) and we will send your Agent
          ID to the email on file.
        </Typography>
      </div>

      <TextField
        label="NPN (National Producer Number)"
        value={npn}
        onChange={(e) => setNpn(e.target.value)}
        error={!!npnError}
        helperText={npnError}
        inputMode="numeric"
        inputProps={{ pattern: "[0-9]*", autoComplete: "off" }}
        fullWidth
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={submitting}
        sx={(theme) => ({
          borderRadius: "5px",
          py: "6px",
          fontSize: "0.875rem",
          backgroundColor: theme.palette.primary.main,
          "&:hover": { backgroundColor: theme.palette.primary.light },
        })}
        startIcon={
          submitting ? (
            <CircularProgress size={14} color="inherit" aria-hidden="true" />
          ) : undefined
        }
      >
        {submitting ? "Submitting…" : "Recover Agent ID"}
      </Button>

      <Link
        component="button"
        type="button"
        variant="body2"
        onClick={() => onNavigate("signin")}
        sx={{ alignSelf: "center" }}
      >
        Back to sign in
      </Link>
    </Stack>
  );
}
